import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Card,
    CardHeader,
    Body1,
    Caption1,
    Tag,
    DataGrid,
    DataGridBody,
    DataGridRow,
    DataGridCell,
    DataGridHeader,
    DataGridHeaderCell,
    TableColumnDefinition,
    createTableColumn,
    mergeClasses,
    Accordion,
    AccordionItem,
    AccordionHeader,
    AccordionPanel,
    TabList,
    Tab,
    Dropdown,
    Option,
    Label,
} from '@fluentui/react-components';
import {
    CheckmarkCircle24Regular,
    ErrorCircle24Regular,
    Tag24Regular,
    Lightbulb24Regular,
    Warning24Regular,
    ArrowReply24Regular,
    ClockAlarm24Regular,
    HandRight24Regular,
} from '@fluentui/react-icons';
import {
    ConversationsSummaryResponse,
    ConversationSetStats,
} from '../../../../model/summaries/ConversationsSummaryResponse';
import { ShiftPeriod } from '../../../../utils/shiftTimes';
import { useConversationsSummaryDialogStyles } from './ConversationsSummaryDialog.styles';
import { useScrollBarStyle } from './../scrollbar.styles';

interface StatusCategoryRow {
    name: string;
    count: number;
}
interface IConversationSetTabProps {
    stats: ConversationSetStats;
    isResolved: boolean;
}
interface ISummaryListSectionProps {
    title: string;
    items: string[] | undefined;
    icon?: React.ReactElement;
}

const SummaryListSection: React.FC<ISummaryListSectionProps> = ({
    title,
    items,
    icon,
}) => {
    const styles = useConversationsSummaryDialogStyles();
    if (!items || items.length === 0) return null;

    return (
        <Accordion collapsible>
            <AccordionItem value={title}>
                <AccordionHeader icon={icon} expandIconPosition="end">
                    <div className={styles.sectionTitle}>{title}</div>
                </AccordionHeader>
                <AccordionPanel>
                    <div className={styles.tagContainer}>
                        {items.map((item, idx) => (
                            <Tag
                                key={`${title}-${idx}`}
                                size="small"
                                shape="rounded"
                            >
                                {item}
                            </Tag>
                        ))}
                    </div>
                </AccordionPanel>
            </AccordionItem>
        </Accordion>
    );
};

const ConversationSetTab: React.FC<IConversationSetTabProps> = ({
    stats,
    isResolved,
}) => {
    const { t } = useTranslation();
    const styles = useConversationsSummaryDialogStyles();
    const scrollBarStyles = useScrollBarStyle();

    const formatMinutes = (seconds: number) => Math.round(seconds / 60);

    // Statuses only shown for open queries
    const hasStatusCounts =
        !isResolved &&
        stats.statusCounts &&
        Object.keys(stats.statusCounts).length > 0;
    const hasCategoryCounts =
        stats.categoryCounts && Object.keys(stats.categoryCounts).length > 0;

    const columns: TableColumnDefinition<StatusCategoryRow>[] = [
        createTableColumn<StatusCategoryRow>({
            columnId: 'name',
            renderHeaderCell: () =>
                t('Dialog_Summary_Name', { defaultValue: 'Name' }),
            renderCell: (item) => item.name,
        }),
        createTableColumn<StatusCategoryRow>({
            columnId: 'count',
            renderHeaderCell: () =>
                t('Dialog_Summary_Count', { defaultValue: 'Count' }),
            renderCell: (item) => item.count,
        }),
    ];

    const renderTable = (rows: StatusCategoryRow[], ariaLabel: string) => (
        <div className={mergeClasses(styles.tableWrapper, scrollBarStyles)}>
            <DataGrid items={rows} columns={columns} sortable>
                <DataGridHeader>
                    <DataGridRow>
                        {({ renderHeaderCell }) => (
                            <DataGridHeaderCell>
                                {renderHeaderCell()}
                            </DataGridHeaderCell>
                        )}
                    </DataGridRow>
                </DataGridHeader>
                <DataGridBody<StatusCategoryRow>>
                    {({ item, rowId }) => (
                        <DataGridRow<StatusCategoryRow> key={rowId}>
                            {({ renderCell }) => (
                                <DataGridCell>{renderCell(item)}</DataGridCell>
                            )}
                        </DataGridRow>
                    )}
                </DataGridBody>
            </DataGrid>
        </div>
    );

    return (
        <div className={styles.tabPanel}>
            <div className={styles.metricsGrid}>
                <Card className={styles.metricCard}>
                    <CardHeader
                        image={<ArrowReply24Regular />}
                        header={
                            <Body1>
                                {t('Dialog_Summary_AverageFirstReplyTime', {
                                    defaultValue: 'Avg. First Reply',
                                })}
                            </Body1>
                        }
                        description={
                            <Caption1 className={styles.metricValue}>
                                {formatMinutes(
                                    stats.averageFirstReplyTimeSeconds
                                )}{' '}
                                {t('Common_Minutes', {
                                    defaultValue: 'mins',
                                })}
                            </Caption1>
                        }
                    />
                </Card>

                <Card className={styles.metricCard}>
                    <CardHeader
                        image={<HandRight24Regular />}
                        header={
                            <Body1>
                                {t('Dialog_Summary_AverageHandlingTime', {
                                    defaultValue: 'Avg. Handling Time',
                                })}
                            </Body1>
                        }
                        description={
                            <Caption1 className={styles.metricValue}>
                                {formatMinutes(
                                    stats.averageHandlingTimeSeconds
                                )}{' '}
                                {t('Common_Minutes', {
                                    defaultValue: 'mins',
                                })}
                            </Caption1>
                        }
                    />
                </Card>

                {isResolved && (
                    <Card className={styles.metricCard}>
                        <CardHeader
                            image={<ClockAlarm24Regular />}
                            header={
                                <Body1>
                                    {t('Dialog_Summary_AverageResolutionTime', {
                                        defaultValue: 'Avg. Resolution',
                                    })}
                                </Body1>
                            }
                            description={
                                <Caption1 className={styles.metricValue}>
                                    {formatMinutes(
                                        stats.averageResolutionTimeSeconds
                                    )}{' '}
                                    {t('Common_Minutes', {
                                        defaultValue: 'mins',
                                    })}
                                </Caption1>
                            }
                        />
                    </Card>
                )}
            </div>

            {(hasStatusCounts || hasCategoryCounts) && (
                <div className={styles.tablesContainer}>
                    {hasStatusCounts && (
                        <section
                            className={styles.tableSection}
                            aria-label="Status breakdown"
                        >
                            <div className={styles.sectionTitle}>
                                {t('Dialog_Summary_DetailedStatus', {
                                    defaultValue: 'Status Breakdown',
                                })}
                            </div>
                            {renderTable(
                                Object.entries(stats.statusCounts)
                                    .sort((a, b) => b[1] - a[1])
                                    .map(([name, count]) => ({ name, count })),
                                'Status breakdown'
                            )}
                        </section>
                    )}

                    {hasCategoryCounts && (
                        <section
                            className={styles.tableSection}
                            aria-label="Category breakdown"
                        >
                            <div className={styles.sectionTitle}>
                                {t('Dialog_Summary_Categories', {
                                    defaultValue: 'Categories',
                                })}
                            </div>
                            {renderTable(
                                Object.entries(stats.categoryCounts)
                                    .sort((a, b) => b[1] - a[1])
                                    .map(([name, count]) => ({ name, count })),
                                'Category breakdown'
                            )}
                        </section>
                    )}
                </div>
            )}
        </div>
    );
};

interface IConversationsSummaryContentProps {
    summary: ConversationsSummaryResponse;
    selectedShift: ShiftPeriod;
    shiftOptions: Array<{ key: ShiftPeriod; label: string }>;
    onShiftChange: (_: any, data: { optionValue?: string }) => void;
}

export const ConversationsSummaryContent: React.FC<
    IConversationsSummaryContentProps
> = ({ summary, selectedShift, shiftOptions, onShiftChange }) => {
    const { t } = useTranslation();
    const styles = useConversationsSummaryDialogStyles();
    const scrollBarStyles = useScrollBarStyle();
    const [selectedTab, setSelectedTab] = useState<'open' | 'resolved'>('open');

    const totalCount =
        (summary.openConversationsStats?.count ?? 0) +
        (summary.resolvedConversationsStats?.count ?? 0);

    return (
        <div className={mergeClasses(styles.contentContainer, scrollBarStyles)}>
            <div className={styles.summaryHeader}>
                <div className={styles.periodField}>
                    <Label htmlFor="summaryPeriodDropdown">
                        {t('Dialog_Summary_ChoosePeriod', {
                            defaultValue: 'Choose time period',
                        })}
                    </Label>
                    <Dropdown
                        id="summaryPeriodDropdown"
                        aria-label={t('Dialog_Summary_ChoosePeriod', {
                            defaultValue: 'Choose time period',
                        })}
                        value={
                            shiftOptions.find((o) => o.key === selectedShift)
                                ?.label ?? shiftOptions[0]?.label
                        }
                        selectedOptions={[selectedShift]}
                        onOptionSelect={onShiftChange}
                        size="medium"
                    >
                        {shiftOptions.map((opt) => (
                            <Option key={opt.key} value={opt.key}>
                                {opt.label}
                            </Option>
                        ))}
                    </Dropdown>
                </div>

                <Card className={styles.totalCard}>
                    <div className={styles.totalCardContent}>
                        <span className={styles.totalCardValue}>
                            {totalCount}
                        </span>
                        <span className={styles.totalCardLabel}>
                            {t('Dialog_Summary_ConversationCount', {
                                defaultValue: 'Conversations',
                            })}
                        </span>
                    </div>
                </Card>
            </div>

            {summary.isAiEnabled && (
                <>
                    <SummaryListSection
                        title={t('Dialog_Summary_KeyThemes', {
                            defaultValue: 'Key Themes',
                        })}
                        items={summary.keyThemes}
                        icon={<Tag24Regular />}
                    />
                    <SummaryListSection
                        title={t('Dialog_Summary_OpenIssues', {
                            defaultValue: 'Open Issues',
                        })}
                        items={summary.openIssues}
                        icon={<Warning24Regular />}
                    />
                    <SummaryListSection
                        title={t('Dialog_Summary_RecommendedAreas', {
                            defaultValue: 'Recommended Priority Areas',
                        })}
                        items={summary.recommendedPriorityAreas}
                        icon={<Lightbulb24Regular />}
                    />
                </>
            )}

            <TabList
                selectedValue={selectedTab}
                onTabSelect={(_, data) =>
                    setSelectedTab(data.value as 'open' | 'resolved')
                }
            >
                <Tab value="open" icon={<ErrorCircle24Regular />}>
                    {t('Dialog_Summary_Tab_Open', {
                        defaultValue: 'Open Queries',
                    })}{' '}
                    ({summary.openConversationsStats?.count ?? 0})
                </Tab>
                <Tab value="resolved" icon={<CheckmarkCircle24Regular />}>
                    {t('Dialog_Summary_Tab_Resolved', {
                        defaultValue: 'Resolved Queries',
                    })}{' '}
                    - {selectedShift} (
                    {summary.resolvedConversationsStats?.count ?? 0})
                </Tab>
            </TabList>

            {selectedTab === 'open' && summary.openConversationsStats && (
                <ConversationSetTab
                    stats={summary.openConversationsStats}
                    isResolved={false}
                />
            )}
            {selectedTab === 'resolved' &&
                summary.resolvedConversationsStats && (
                    <ConversationSetTab
                        stats={summary.resolvedConversationsStats}
                        isResolved={true}
                    />
                )}
        </div>
    );
};
